<script setup lang="ts">
import { nextTick, onBeforeUnmount, onMounted } from 'vue';
import { v4 } from 'uuid';
import {
  Contexts,
  NamedBindings,
  NewCaseComponent,
  TranslationService,
  getContainer,
  ContainerTypes,
  CurrentCaseServiceInterface,
  getContext,
  ApplicationEvents,
  CaseCreated,
  CaseCreationFailed,
  LoggerInterface,
} from '@dandelion/core';
import ReadmissionUserDialog from './ReadmissionUserDialog.vue';
import { Messages } from '../enum/Messages';
import router from '../router';
import { UserService } from '../services/UserService';

const container = getContainer(Contexts.DEFAULT);
const ts = container.get(
  NamedBindings.TranslationService
) as TranslationService;
const userService = getContainer(Contexts.SHARED).get(
  NamedBindings.UserService
) as UserService;
const newCaseService = getContainer(
  Contexts.DEFAULT,
  ContainerTypes.NEW_CASE
).get(
  NamedBindings.CurrentCaseServiceForNewCase
) as CurrentCaseServiceInterface;
const logger = container.get(NamedBindings.Logger) as LoggerInterface;
const user = userService.getCurrentUser();
let caseCreatedToken: string;
let caseCreationFailedToken: string;
let caseCreationCanceledToken: string;
newCaseService.open(`A-${user?.value?.centre}-${v4()}`);
newCaseService.isCaseOpened;

onMounted(() => {
  caseCreatedToken = getContext(Contexts.DEFAULT).addEventListener(
    ApplicationEvents.CaseCreated,
    async (_m: string, e: Event) => {
      const event: CaseCreated = e as CaseCreated;
      const currentCaseService = container.get(
        NamedBindings.CurrentCaseService
      ) as CurrentCaseServiceInterface;
      setTimeout(() => {
        const toast_ = document.querySelector('.Toastify__toast-container');
        if (!toast_) {
          logger.logSuccess(
            ts.t(Messages.NEW_CASE_CREATED) +
              (event.caseID?.substring(0, 14) ?? ''),
            {}
          );
        }
      }, 100);
      await currentCaseService.open(event.caseID);
    }
  );
  caseCreationFailedToken = getContext(Contexts.DEFAULT).addEventListener(
    ApplicationEvents.CaseCreationFailed,
    async (_m: string, e: Event) => {
      const event: CaseCreationFailed = e as CaseCreationFailed;
      logger.logError(
        ts.t(Messages.CASE_CREATION_FAILED) +
          (event.caseID?.substring(0, 14) ?? ''),
        {}
      );
    }
  );
  caseCreationCanceledToken = getContext(Contexts.DEFAULT).addEventListener(
    ApplicationEvents.CaseCreationCanceled,
    async (_e: any) => {
      await nextTick(() => router.push(`/`));
    }
  );
});

onBeforeUnmount(() => {
  getContext(Contexts.DEFAULT).removeEventListenerByToken(caseCreatedToken);
  getContext(Contexts.DEFAULT).removeEventListenerByToken(
    caseCreationFailedToken
  );
  getContext(Contexts.DEFAULT).removeEventListenerByToken(
    caseCreationCanceledToken
  );
});
</script>

<template>
  <ReadmissionUserDialog></ReadmissionUserDialog>
  <main id="nccw">
    <header>
      <h2 v-html="ts.t('Adding new Patient')"></h2>
      <section id="buttonsContainer"></section>
      <p v-html="ts.t('You must fill in all fields')"></p>
    </header>
    <div class="divider mt-2"></div>
    <Suspense>
      <NewCaseComponent crf-name="core" />
    </Suspense>
  </main>
</template>

<style>
.button-spinner {
  width: 0.75rem;
  height: 0.75rem;
  border: 3px solid rgba(255, 255, 255, 0.2);
  border-top: 3px solid #fff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }

  100% {
    transform: rotate(360deg);
  }
}
</style>
